import { isEmbedded } from '~/utils/helpers';

/**
 * Whether this build may only run inside a host application.
 *
 * Driven by REACT_APP_EMBEDDED_ONLY so it can be turned on per environment and
 * turned back off without a code change. Anything other than the exact string
 * 'true' leaves the gate open, so a typo cannot lock an environment out by
 * accident -- the deployed .env files set it explicitly rather than relying on
 * a default either way.
 *
 * @returns {Boolean} true when standalone browser access is disabled
 */
export const isEmbeddedOnly = (): boolean =>
    (process.env.REACT_APP_EMBEDDED_ONLY || '').trim().toLowerCase() === 'true';

/**
 * Whether this document is being served from the developer's own machine.
 *
 * The gate is there to stop members reaching Welfare outside a host, and a
 * localhost origin is not a member -- it is whoever is running the dev server.
 * Without this, working on Welfare on its own means standing up the whole web
 * app to frame it, or editing .env and remembering to put it back.
 *
 * Costs nothing in a deployed environment: no browser resolves a public host
 * name to these, so the branch is unreachable anywhere it would matter. It is
 * host-based rather than NODE_ENV-based deliberately -- a production build
 * served locally (`serve -s build`) is exactly the thing worth testing, and
 * NODE_ENV would refuse it.
 *
 * @returns {Boolean} true on localhost, 127.0.0.1 or ::1
 */
const isLocalhost = (): boolean => {
    const { hostname } = window.location;

    return (
        hostname === 'localhost' ||
        hostname.endsWith('.localhost') ||
        hostname === '127.0.0.1' ||
        hostname === '[::1]' ||
        hostname === '::1'
    );
};

/**
 * Whether the app should refuse to run in the window it finds itself in.
 *
 * True only when the build is embedded-only, there is no host framing us -- i.e.
 * someone opened the Welfare app directly in a browser tab -- and we are not
 * running on the developer's own machine.
 *
 * Note what this is and is not. Combined with the login routes being
 * unreachable behind the gate, it is effective: a session can only come from a
 * handoff token, and those are only handed over by an allowlisted origin
 * (REACT_APP_HANDOFF_PARENT_ORIGINS) or the mobile WebView bridge, so a direct
 * visitor has no way to authenticate. On its own, though, `isEmbedded()` is not
 * a security boundary -- it is true inside *any* iframe, including one on a
 * page we do not control. Restricting who may frame this app is a
 * `frame-ancestors` / X-Frame-Options question for the web server, not
 * something this file can answer.
 *
 * @returns {Boolean} true when direct access should be blocked
 */
export const isDirectAccessBlocked = (): boolean =>
    isEmbeddedOnly() && !isEmbedded() && !isLocalhost();
